/**
 * countdown-engine.js
 *
 * Shared countdown logic used by count-timer.liquid, timer.liquid
 * ("Countdown Timer Banner"), and devx-timer.liquid (the cart-strip
 * countdown). These were 3 separate, duplicated implementations of the
 * same parse/tick/pad/render loop; this consolidates the engine only —
 * each component's own Liquid markup, CSS, and settings are unchanged.
 *
 * End date resolution (checked in this order on the root element):
 *   - data-end-iso                          ISO 8601 string
 *   - data-end                              'YYYY-MM-DD HH:MM:SS'
 *                                            (space swapped for 'T')
 *   - data-end-raw                          any string passed to
 *                                            `new Date()` unmodified
 *                                            (e.g. 'oct 12, 2025 09:59:59')
 *   - data-year/-month/-day/-hour/-minute   separate numeric fields
 *
 * Options (data attributes on the root element):
 *   - data-daily-reset="true"    Once expired, advance the target by
 *                                whole days until back in the future,
 *                                then keep counting down. Off by
 *                                default so one-time sale banners
 *                                don't silently recur — opt in per
 *                                component.
 *   - data-render-mode="digits"  Render each digit of every unit in
 *                                its own child element (pre-existing
 *                                markup with exactly N child elements
 *                                per unit) instead of writing the
 *                                padded string as one text node
 *                                (the default).
 *   - data-hide-until-valid="true"  Keep the root element hidden
 *                                (via the "hide" class) until a valid,
 *                                still-future end date is found; used
 *                                by devx-timer so the cart strip stays
 *                                collapsed when no countdown is active.
 *
 * Unit elements are located via [data-role="hours|minutes|seconds"]
 * inside the root element.
 */
(function () {
  'use strict';

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function parseEndDate(root) {
    var iso = root.getAttribute('data-end-iso');
    if (iso) return new Date(iso);

    var end = root.getAttribute('data-end');
    if (end) return new Date(end.replace(' ', 'T'));

    var endRaw = root.getAttribute('data-end-raw');
    if (endRaw) return new Date(endRaw);

    var year = root.getAttribute('data-year');
    var month = root.getAttribute('data-month');
    var day = root.getAttribute('data-day');
    var hour = root.getAttribute('data-hour');
    var minute = root.getAttribute('data-minute');
    if (year && month && day) {
      return new Date(
        parseInt(year, 10),
        parseInt(month, 10) - 1, // JS months are 0-based
        parseInt(day, 10),
        hour ? parseInt(hour, 10) : 0,
        minute ? parseInt(minute, 10) : 0,
        0, 0
      );
    }

    return null;
  }

  function writeUnit(el, valueStr, digitMode) {
    if (!el) return;
    if (!digitMode) {
      if (el.textContent !== valueStr) el.textContent = valueStr;
      return;
    }
    var digitEls = el.children;
    for (var i = 0; i < digitEls.length && i < valueStr.length; i++) {
      var ch = valueStr[i];
      if (digitEls[i].textContent !== ch) digitEls[i].textContent = ch;
    }
  }

  function CountdownEngine(root) {
    this.root = root;
    this.dailyReset = root.getAttribute('data-daily-reset') === 'true';
    this.digitMode = root.getAttribute('data-render-mode') === 'digits';
    this.hideUntilValid = root.getAttribute('data-hide-until-valid') === 'true';

    this.hoursEl = root.querySelector('[data-role="hours"]');
    this.minutesEl = root.querySelector('[data-role="minutes"]');
    this.secondsEl = root.querySelector('[data-role="seconds"]');

    this.endDate = parseEndDate(root);
    this.intervalId = null;
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.tick = this.tick.bind(this);
  }

  CountdownEngine.prototype.moveToNextValidDate = function (now) {
    if (!this.dailyReset) return;
    while (this.endDate <= now) {
      this.endDate.setDate(this.endDate.getDate() + 1);
    }
  };

  CountdownEngine.prototype.tick = function () {
    if (!this.endDate || isNaN(this.endDate.getTime())) return;

    var now = new Date();

    if (now >= this.endDate) {
      this.moveToNextValidDate(now);
    }

    var diff = this.endDate - now;

    if (diff <= 0) {
      writeUnit(this.hoursEl, '00', this.digitMode);
      writeUnit(this.minutesEl, '00', this.digitMode);
      writeUnit(this.secondsEl, '00', this.digitMode);
      this.stop();
      this.root.dispatchEvent(new CustomEvent('countdown:expired', { bubbles: true }));
      return;
    }

    if (this.hideUntilValid) this.root.classList.remove('hide');

    var totalSeconds = Math.floor(diff / 1000);
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;

    writeUnit(this.hoursEl, pad(hours), this.digitMode);
    writeUnit(this.minutesEl, pad(minutes), this.digitMode);
    writeUnit(this.secondsEl, pad(seconds), this.digitMode);
  };

  CountdownEngine.prototype.onVisibilityChange = function () {
    if (document.hidden) {
      this.stop();
    } else {
      this.tick();
      this.intervalId = setInterval(this.tick, 1000);
    }
  };

  CountdownEngine.prototype.start = function () {
    if (!this.endDate || isNaN(this.endDate.getTime())) return;
    if (!this.hoursEl || !this.minutesEl || !this.secondsEl) return;

    this.moveToNextValidDate(new Date());
    this.tick(); // run once immediately, no 1s blank flash
    this.intervalId = setInterval(this.tick, 1000);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  };

  CountdownEngine.prototype.stop = function () {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  };

  CountdownEngine.prototype.destroy = function () {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  };

  window.theme = window.theme || {};
  window.theme.CountdownEngine = CountdownEngine;
})();
